// "Call held offline — continue" / "Held offline — continue" (lane B13, F-158; DS DC-237, contract C5). Wherever a step
// waits for a meeting to be held, its scheduling card carries a ghost button that records the meeting held without a
// booked event, through the step's existing held tool (intake.record_held, round.mark_held, debrief.record_attendance),
// so nothing in the hiring flow waits on the calendar integration. The dialog (#60) is titled with the button's words
// before the dash: *Held at* (a date and time, now unless changed, never later than now), *Attendees* (the meeting's
// parties, ticked; editable) and *Reason* (required, 3–500 characters, the counter from the tool's own limit); primary
// *Record and continue*. An empty reason reads *Required* under Reason before anything is sent; the server's field error
// lands on its field; any other refusal is the red banner inside the dialog with what was entered kept.

import { useState } from "react";
import { AppError, businessLocalToISO, clockNow, isoToBusinessLocal } from "@/api/runtime";
import { inputLengthHint, inputMaxLength } from "@/api/inputLimits";
import { errorText } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { Button, CheckTile, COMMON_LABELS, DIFF_LABELS, Dialog, Field, Input, Pill, ROLE_LABELS, SCHEDULING_LABELS, Stack, TextArea } from "@/mc";
import { HELD_OFFLINE_LABELS } from "./labels";

/** One party of the meeting: the intake call's seat holders (views/plan/RecordHeld's HeldAttendee fits), a round's
 *  interviewer or a debrief's required attendee. `seat` is the role key the party sits in. */
export interface OfflineParty {
  person_id: string;
  name: string;
  seat?: string | undefined;
  /** A required party (the intake call: the Hiring Manager and the Recruiter). */
  required?: boolean | undefined;
}

/** What the step's held tool takes from the dialog. */
export interface OfflineRecord {
  /** ISO instant, never later than now. */
  held_at: string;
  offline_reason: string;
  /** Who attended, by person id. */
  present: Readonly<Record<string, boolean>>;
}

const REASON_MIN = 3;
const REASON_MAX = 500;

const SEAT_WORD: Readonly<Record<string, string>> = {
  hm: ROLE_LABELS.hm, recruiter: ROLE_LABELS.recruiter, hrbp: ROLE_LABELS.hrbp, interviewer: ROLE_LABELS.interviewer,
};

/** The intake call's rule (CR-018): the Hiring Manager and the Recruiter are required, the HR Business Partner optional. */
export function isRequiredParty(party: OfflineParty): boolean {
  return party.required ?? (party.seat === "hm" || party.seat === "recruiter");
}

/** The button's words: the intake call's, or every other meeting's. */
export function heldOfflineButton(intake: boolean): string {
  return intake ? HELD_OFFLINE_LABELS.callHeldOfflineContinue : HELD_OFFLINE_LABELS.heldOfflineContinue;
}

/** The ghost button that opens the dialog. */
export function HeldOfflineButton({ intake, onOpen, hs }: { intake: boolean; onOpen: () => void; hs?: string | undefined }) {
  return (
    <div>
      <Button tone="ghost" icon="check" onClick={onOpen} hs={hs ?? "sched.offline"}>{heldOfflineButton(intake)}</Button>
    </div>
  );
}

export interface HeldOfflineDialogProps {
  /** The intake call (its words) or another meeting. */
  intake: boolean;
  parties: readonly OfflineParty[];
  /** The step's held tool; a rejection is the server's refusal. */
  record: (input: OfflineRecord) => Promise<unknown>;
  onClose: () => void;
  /** The meeting is recorded held offline, with this reason. */
  onRecorded?: ((reason: string) => void) | undefined;
  /** The tool whose `offline_reason` limit the counter reads (default the intake's). */
  tool?: string | undefined;
}

/** The wall-clock time a business-zone datetime-local control shows for now. */
function nowLocal(): string {
  return isoToBusinessLocal(new Date(clockNow()).toISOString());
}

export function HeldOfflineDialog({ intake, parties, record, onClose, onRecorded, tool = "intake.record_held" }: HeldOfflineDialogProps) {
  const { manifest } = useSession();
  const [when, setWhen] = useState(nowLocal);
  const [reason, setReason] = useState("");
  const [present, setPresent] = useState<Record<string, boolean>>(() => Object.fromEntries(parties.map((p) => [p.person_id, true])));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const limit = inputMaxLength(manifest, tool, "offline_reason") ?? REASON_MAX;
  const heldAt = businessLocalToISO(when);
  const future = heldAt !== "" && Date.parse(heldAt) > clockNow();
  const trimmed = reason.trim();
  const fieldErrors = failure instanceof AppError ? failure.fieldErrors : {};
  const reasonError = (tried && trimmed.length === 0) || fieldErrors["offline_reason"] === "required"
    ? COMMON_LABELS.required
    : fieldErrors["offline_reason"] ? DIFF_LABELS.reason : undefined;
  const heldAtError = future || fieldErrors["held_at"] ? HELD_OFFLINE_LABELS.futureHeldAt : undefined;
  const fieldRefusal = !!fieldErrors["offline_reason"] || !!fieldErrors["held_at"];
  const ready = trimmed.length >= REASON_MIN && trimmed.length <= limit && heldAt !== "" && !future;

  const submit = () => {
    setTried(true);
    if (busy || !ready) return;
    setBusy(true);
    setFailure(null);
    record({ held_at: heldAt, offline_reason: trimmed, present })
      .then(() => onRecorded?.(trimmed))
      .catch((error: unknown) => setFailure(error))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog
      title={intake ? HELD_OFFLINE_LABELS.callHeldOffline : HELD_OFFLINE_LABELS.heldOffline}
      open
      onClose={() => onClose()}
      refused={failure !== null && !fieldRefusal ? { reason: errorText(failure) } : undefined}
      primary={{
        label: HELD_OFFLINE_LABELS.recordAndContinue,
        onClick: submit,
        busy,
        blocked: ready || !tried ? undefined : { reason: `${HELD_OFFLINE_LABELS.heldAt} · ${DIFF_LABELS.reason}` },
        hs: "sched.offline.submit",
      }}
      
    >
      <Stack gap={12}>
        <Field label={HELD_OFFLINE_LABELS.heldAt} required error={heldAtError}>
          <Input type="datetime-local" value={when} max={nowLocal()} onChange={(event) => setWhen(event.target.value)}  />
        </Field>
        {parties.length ? (
          <Field label={SCHEDULING_LABELS.attendees}>
            <Stack gap={8}>
              {parties.map((party) => (
                <CheckTile
                  key={party.person_id}
                  checked={present[party.person_id] ?? true}
                  onChange={(checked) => setPresent((was) => ({ ...was, [party.person_id]: checked }))}
                  sub={party.seat ? SEAT_WORD[party.seat] ?? undefined : undefined}
                  right={isRequiredParty(party) ? <Pill tone="warn">{COMMON_LABELS.required}</Pill> : undefined}
                  hs={`sched.offline.attendee.${party.seat ?? "party"}`}
                >
                  {party.name}
                </CheckTile>
              ))}
            </Stack>
          </Field>
        ) : null}
        <Field label={DIFF_LABELS.reason} required error={reasonError} hint={inputLengthHint(reason, limit)}>
          <TextArea rows={3} value={reason} maxLength={limit} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}
